import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, RefreshCw, Sun } from 'lucide-react';
import { loadEphemerisData, findClosestEphemerisEntry } from '../../astro/ephemerisUtils';
import useCityToCoordinates from './useCityToCoordinates';

const majorCities = [
  { name: "New York, NY, USA", lat: 40.7128, lon: -74.0060 },
  { name: "Los Angeles, CA, USA", lat: 34.0522, lon: -118.2437 },
  { name: "Chicago, IL, USA", lat: 41.8781, lon: -87.6298 },
  { name: "London, UK", lat: 51.5074, lon: -0.1278 },
  { name: "Paris, France", lat: 48.8566, lon: 2.3522 },
  { name: "Berlin, Germany", lat: 52.52, lon: 13.405 },
  { name: "Amsterdam, Netherlands", lat: 52.3676, lon: 4.9041 },
  { name: "Tokyo, Japan", lat: 35.6762, lon: 139.6503 },
  { name: "Sydney, Australia", lat: -33.8688, lon: 151.2093 },
  { name: "Toronto, Canada", lat: 43.6532, lon: -79.3832 },
  { name: "Mexico City, Mexico", lat: 19.4326, lon: -99.1332 },
  { name: "Buenos Aires, Argentina", lat: -34.6118, lon: -58.396 },
  { name: "Cape Town, South Africa", lat: -33.9249, lon: 18.4241 },
  { name: "Moscow, Russia", lat: 55.7558, lon: 37.6176 },
  { name: "Beijing, China", lat: 39.9042, lon: 116.4074 },
  { name: "Delhi, India", lat: 28.7041, lon: 77.1025 },
  { name: "Bangkok, Thailand", lat: 13.7563, lon: 100.5018 },
  { name: "Rio de Janeiro, Brazil", lat: -22.9068, lon: -43.1729 }
];

const SaturnReturnExperience = () => {
  const [page, setPage] = useState(0);
  const [formData, setFormData] = useState({
    birthDate: '',
    birthTime: '',
    birthPlace: '',
    latitude: '',
    longitude: ''
  });
  const [isCustomPlace, setIsCustomPlace] = useState(false);
  const [customPlace, setCustomPlace] = useState('');
  const [showCoordinates, setShowCoordinates] = useState(false);
  const [saturnReturnData, setSaturnReturnData] = useState(null);
  const [loading, setLoading] = useState(false);
  const { fetchCoordinates, loading: geocodingLoading, error: geocodingError } = useCityToCoordinates();

  const handleChange = ({ target: { name, value } }) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePlaceChange = async (e) => {
    const selectedValue = e.target.value;
    if (selectedValue === 'custom') {
      setIsCustomPlace(true);
      setShowCoordinates(false);
      setFormData(prev => ({ ...prev, birthPlace: '', latitude: '', longitude: '' }));
    } else if (selectedValue === '') {
      setIsCustomPlace(false);
      setShowCoordinates(false);
      setFormData(prev => ({ ...prev, birthPlace: '', latitude: '', longitude: '' }));
    } else {
      setIsCustomPlace(false);
      const selectedCity = majorCities.find(city => city.name === selectedValue);
      if (selectedCity) {
        setFormData(prev => ({
          ...prev,
          birthPlace: selectedCity.name,
          latitude: selectedCity.lat.toString(),
          longitude: selectedCity.lon.toString()
        }));
        setShowCoordinates(true);
      }
    }
  };

  const handleCustomPlaceSubmit = async () => {
    if (!customPlace.trim()) return;
    try {
      const coords = await fetchCoordinates(customPlace);
      if (coords) {
        setFormData(prev => ({
          ...prev,
          birthPlace: customPlace,
          latitude: coords.lat.toString(),
          longitude: coords.lon.toString()
        }));
        setShowCoordinates(true);
        setIsCustomPlace(false);
        setCustomPlace('');
      }
    } catch (error) {}
  };

  // Find the next Saturn return date after birth
  const findNextSaturnReturn = async (birthDate, natalSaturnLongitude) => {
    let year = birthDate.getFullYear() + 27; // Saturn return is ~29.5 years, start searching a bit before
    const maxYears = 40; // Search up to 40 years after birth
    for (let i = 0; i < maxYears; i++) {
      const ephemerisData = await loadEphemerisData(year);
      if (!ephemerisData) continue;
      for (const entry of ephemerisData) {
        if (!entry.planets || !entry.planets.Saturn) continue;
        const saturnLon = entry.planets.Saturn.longitude;
        // If Saturn is within 1 degree of natal position, consider it a return
        if (Math.abs(((saturnLon - natalSaturnLongitude + 180) % 360) - 180) < 1) {
          return { date: entry.date, longitude: saturnLon };
        }
      }
      year++;
    }
    return null;
  };

  const getZodiacSign = (longitude) => {
    const signs = [
      'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
      'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'
    ];
    const signIndex = Math.floor(longitude / 30);
    return signs[signIndex];
  };

  const getSignEmoji = (sign) => {
    const signEmojis = {
      'Aries': '♈', 'Taurus': '♉', 'Gemini': '♊', 'Cancer': '♋',
      'Leo': '♌', 'Virgo': '♍', 'Libra': '♎', 'Scorpio': '♏',
      'Sagittarius': '♐', 'Capricorn': '♑', 'Aquarius': '♒', 'Pisces': '♓'
    };
    return signEmojis[sign] || '⭐';
  };

  const formatLongitude = (longitude) => {
    const degrees = Math.floor(longitude);
    const minutes = Math.floor((longitude - degrees) * 60);
    const sign = getZodiacSign(longitude);
    return `${degrees}° ${minutes}' ${sign}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const birthDate = new Date(`${formData.birthDate}T${formData.birthTime}`);
      const birthYear = birthDate.getFullYear();
      // Get natal Saturn longitude
      const natalEphemeris = await loadEphemerisData(birthYear);
      const natalEntry = findClosestEphemerisEntry(natalEphemeris, birthDate);
      if (!natalEntry || !natalEntry.planets || !natalEntry.planets.Saturn) throw new Error('No natal Saturn data');
      const natalSaturnLongitude = natalEntry.planets.Saturn.longitude;
      // Find next Saturn return
      const saturnReturn = await findNextSaturnReturn(birthDate, natalSaturnLongitude);
      if (!saturnReturn) throw new Error('No Saturn return found');
      setSaturnReturnData({
        birthDate: formData.birthDate,
        birthTime: formData.birthTime,
        birthPlace: formData.birthPlace,
        saturnReturnDate: saturnReturn.date,
        saturnLongitude: saturnReturn.longitude,
        saturnSign: getZodiacSign(saturnReturn.longitude),
        saturnEmoji: getSignEmoji(getZodiacSign(saturnReturn.longitude)),
        natalSaturnLongitude,
        natalSaturnSign: getZodiacSign(natalSaturnLongitude),
        natalSaturnEmoji: getSignEmoji(getZodiacSign(natalSaturnLongitude))
      });
      setPage(1);
    } catch (error) {
      console.error('Error calculating Saturn return:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="full-screen-image-container full-height margin-top-0"
      style={{ backgroundImage: `url('/img/saturnwallpaper.jpg')` }}
    >
      {page === 0 && (
        <div className="ascendant-display-container">
          <div className="ascendant-header">
            <div className="ascendant-title">
              <Sun size={24} />
              <h1>Saturn Return Chart</h1>
            </div>
          </div>
          <form onSubmit={handleSubmit} className="ascendant-form">
            <div className="form-section" style={{
              background: 'rgba(0, 0, 0, 0.4)',
              borderRadius: '16px',
              padding: '2rem',
              marginBottom: '2rem',
              border: '1px solid rgba(255, 215, 0, 0.2)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
            }}>
              <div className="section-title" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                marginBottom: '1.5rem',
                color: '#FFD700',
                fontSize: '1.2rem',
                fontWeight: '600'
              }}>
                <Calendar size={20} />
                <span>Birth Information</span>
              </div>
              <div className="form-row">
                <div className="form-field">
                  <label>Birth Date</label>
                  <input
                    type="date"
                    name="birthDate"
                    value={formData.birthDate}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-field">
                  <label>Birth Time</label>
                  <input
                    type="time"
                    name="birthTime"
                    value={formData.birthTime}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
              <div className="form-field">
                <label>Birth Place</label>
                <select value={isCustomPlace ? 'custom' : formData.birthPlace} onChange={handlePlaceChange} required>
                  <option value="">Select a city...</option>
                  {majorCities.map(city => (
                    <option key={city.name} value={city.name}>{city.name}</option>
                  ))}
                  <option value="custom">Custom...</option>
                </select>
                {isCustomPlace && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <input type="text" value={customPlace} onChange={e => setCustomPlace(e.target.value)} placeholder="Enter custom place" />
                    <button type="button" onClick={handleCustomPlaceSubmit} disabled={geocodingLoading} style={{ marginLeft: 8 }}>
                      {geocodingLoading ? 'Locating...' : 'Set Location'}
                    </button>
                    {geocodingError && <div style={{ color: 'red', marginTop: 4 }}>{geocodingError}</div>}
                  </div>
                )}
              </div>
            </div>
            <div className="form-actions">
              <button
                type="submit"
                className="calculate-button"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <RefreshCw size={18} className="spinning" />
                    Calculating...
                  </>
                ) : (
                  <>
                    <Sun size={18} />
                    Calculate Saturn Return
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}
      {page === 1 && saturnReturnData && (
        <motion.div
          key="result"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 50 }}
          transition={{ duration: 0.4 }}
          className="ascendant-display-container"
        >
          <div className="ascendant-result">
            <div className="result-header">
              <h2>Saturn Return Chart</h2>
              <div className="ascendant-sign-display">
                <span className="sign-emoji" style={{ fontSize: '4rem' }}>♄</span>
                <span className="sign-name">{saturnReturnData.saturnReturnDate}</span>
              </div>
            </div>
            <div className="result-details">
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                margin: '1.5rem 0 2.5rem 0',
                gap: '1.5rem',
              }}>
                <div style={{
                  flex: 1,
                  padding: '1rem',
                  borderRadius: '12px',
                  background: 'rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  textAlign: 'center',
                  color: '#fff',
                  fontWeight: 500,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}>
                  <span style={{ opacity: 0.7, fontSize: '0.95rem', marginBottom: '0.25rem' }}>Saturn Return Date</span>
                  <span style={{ fontWeight: 700, fontSize: '1.15rem' }}>{saturnReturnData.saturnReturnDate}</span>
                </div>
                <div style={{
                  flex: 1,
                  padding: '1rem',
                  borderRadius: '12px',
                  background: 'rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  textAlign: 'center',
                  color: '#fff',
                  fontWeight: 500,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}>
                  <span style={{ opacity: 0.7, fontSize: '0.95rem', marginBottom: '0.25rem' }}>Birth Date</span>
                  <span style={{ fontWeight: 700, fontSize: '1.15rem' }}>{saturnReturnData.birthDate}</span>
                </div>
                <div style={{
                  flex: 1,
                  padding: '1rem',
                  borderRadius: '12px',
                  background: 'rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  textAlign: 'center',
                  color: '#fff',
                  fontWeight: 500,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}>
                  <span style={{ opacity: 0.7, fontSize: '0.95rem', marginBottom: '0.25rem' }}>Birth Time</span>
                  <span style={{ fontWeight: 700, fontSize: '1.15rem' }}>{saturnReturnData.birthTime}</span>
                </div>
              </div>
            </div>
            <div className="ascendant-description">
              <h3>What is a Saturn Return?</h3>
              <p>
                A Saturn Return occurs when Saturn returns to the same zodiac position it occupied at your birth. This major astrological event happens roughly every 29.5 years and marks a period of maturity, responsibility, and life lessons. Your first Saturn return is a rite of passage into adulthood, and each return brings new growth and transformation.
              </p>
            </div>
          </div>
          <motion.button
            className="button is-warning"
            style={{
              position: "fixed",
              top: 100,
              left: 24,
              zIndex: 1000,
              boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
              borderRadius: "12px",
              padding: "12px 16px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontSize: "14px",
              fontWeight: "600",
              border: "none",
              background: "linear-gradient(135deg, #ffc107 0%, #ff8f00 100%)",
              color: "#000",
              cursor: "pointer",
              transition: "all 0.3s ease"
            }}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            onClick={() => window.location.href = "/saturnreturn"}
            onMouseEnter={(e) => {
              e.target.style.transform = "translateY(-2px)";
              e.target.style.boxShadow = "0 6px 16px rgba(0,0,0,0.4)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = "0 4px 12px rgba(0,0,0,0.3)";
            }}
          >
            <ArrowLeft size={20} />
            Back
          </motion.button>
        </motion.div>
      )}
    </div>
  );
};

export default SaturnReturnExperience;
